// src/pages/SSHCertControl.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Typography,
  Input,
  Button,
  Tabs,
  Table,
  Modal,
  Form,
  Select,
  Tag,
  message,
  Divider,
  Space,
  Tooltip,
  Dropdown,
  Drawer,
  Grid,
  theme,
  Checkbox,
  DatePicker,
  Flex,
} from "antd";
import {
  KeyOutlined,
  SafetyOutlined,
  IdcardOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
  MoreOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useDataProvider, useNotification } from "@refinedev/core";
import { usePGBroadcastRefresh } from "../realtime/usePGBroadcastRefresh";

import { Card, Popconfirm } from "antd";
import { ShareAltOutlined } from "@ant-design/icons";

dayjs.extend(utc);

const SSH_SCHEMA = "ssh";
const { Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

/**
 * UI Configuration Knobs
 */
const UI = {
  PAGE_MIN_WIDTH_PX: 320,
  SEARCH_MAX_WIDTH_PX: 460,
  ENABLE_TAB_SCROLL: true,
  ENABLE_MOBILE_TABLE_SCROLL: false,
  DRAWER_MOBILE_WIDTH: "100%" as const,
  MODAL_DESKTOP_WIDTH_PX: 720,
  MODAL_WIDE_WIDTH_PX: 980,
};

function useIsMobile() {
  const screens = useBreakpoint();
  return !screens.sm;
}

const TopFocusAnchor: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timer = setTimeout(
      () => ref.current?.focus({ preventScroll: false }),
      50,
    );
    return () => clearTimeout(timer);
  }, []);
  return (
    <div
      ref={ref}
      tabIndex={-1}
      style={{ outline: "none", height: 0, width: 0, overflow: "hidden" }}
      aria-hidden="true"
    />
  );
};

interface ResponsiveEditorProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  footer?: React.ReactNode;
  onOk?: () => void;
  okText?: string;
  cancelText?: string;
  children: React.ReactNode;
  width?: number | string;
}

const ResponsiveEditor: React.FC<ResponsiveEditorProps> = ({
  open,
  onClose,
  title,
  footer,
  onOk,
  okText = "Save",
  cancelText = "Cancel",
  children,
  width = UI.MODAL_DESKTOP_WIDTH_PX,
}) => {
  const isMobile = useIsMobile();

  const content = (
    <>
      <TopFocusAnchor />
      {children}
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        title={title}
        width={UI.DRAWER_MOBILE_WIDTH}
        placement="left"
        destroyOnClose
        extra={
          footer !== null && (
            <Space>
              <Button onClick={onClose}>{cancelText}</Button>
              {onOk && (
                <Button type="primary" onClick={onOk}>
                  {okText}
                </Button>
              )}
            </Space>
          )
        }
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width={width}
      destroyOnClose
      onOk={onOk}
      okText={okText}
      cancelText={cancelText}
      footer={footer}
      styles={{ body: { maxHeight: "80vh", overflowY: "auto" } }}
    >
      {content}
    </Modal>
  );
};

// --- Utils ---
function filterRows<T extends Record<string, any>>(rows: T[], search: string) {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) =>
    Object.values(row).some(
      (v) => v != null && String(v).toLowerCase().includes(q),
    ),
  );
}

function copyToClipboard(text: string) {
  if (!text) return;
  navigator.clipboard.writeText(text).then(
    () => message.success("Copied"),
    () => message.error("Copy failed"),
  );
}

function fmtTime(v?: string) {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : String(v);
}

function validityTag(row: any) {
  const now = dayjs();
  const va = dayjs(row.valid_after);
  const vb = dayjs(row.valid_before);
  if (!va.isValid() || !vb.isValid()) return <Tag>—</Tag>;
  if (now.isBefore(va))
    return (
      <Tag icon={<ClockCircleOutlined />} color="blue">
        Not yet
      </Tag>
    );
  if (now.isAfter(vb))
    return (
      <Tag icon={<StopOutlined />} color="default">
        Expired
      </Tag>
    );
  return (
    <Tag icon={<CheckCircleOutlined />} color="green">
      Active
    </Tag>
  );
}

function statusTag(status?: string) {
  const s = (status || "").toLowerCase();
  if (s === "issued") return <Tag color="green">issued</Tag>;
  if (s === "pending") return <Tag color="gold">pending</Tag>;
  if (s === "revoked") return <Tag color="red">revoked</Tag>;
  if (s === "expired") return <Tag>expired</Tag>;
  return <Tag>{status || "—"}</Tag>;
}

type CaRow = {
  id: string;
  name: string;
  ca_type: "host" | "user";
  key_type: string;
  status: string;
  ca_public_key: string;
  ca_public_fingerprint?: string | null;
};

type KeyRow = {
  id: string;
  identity: string;
  name: string;
  key_type: string;
  public_key: string;
  public_key_fingerprint?: string | null;
  note?: string | null;
};

type CertRow = {
  id: string;
  ca_id: string;
  subject_key_id: string;
  cert_type: "host" | "user";
  identity: string;
  principals: string[];
  key_id: string;
  serial: number;
  valid_after: string;
  valid_before: string;
  status: "pending" | "issued" | "revoked" | "expired";
  cert_public_key: string;
  cert_fingerprint?: string | null;
  note?: string | null;
  created_at: string;
};

// =====================
// SHARES BROWSER (GLOBAL)
// =====================

type GroupRow = { id: string; name: string };

type ShareRow = {
  id: string;
  group_id: string;
  can_read: boolean;
  can_create: boolean;
  can_update: boolean;
  can_delete: boolean;
  can_admin: boolean;
  created_at?: string;

  // dynamic FK for join-ish display (either ca_id or key_id)
  ca_id?: string;
  key_id?: string;
};

type ParentRow = {
  id: string;
  label: string;
};

function isWritableShare(s: ShareRow) {
  return !!(s.can_create || s.can_update || s.can_delete || s.can_admin);
}

const SharesBrowser: React.FC<{
  title: string;
  kind: "ca" | "key";
  shareResource: "v_ssh_ca_shares" | "ssh_key_shares";
  fkField: "ca_id" | "key_id";
  parentResource: "v_ssh_certificate_authorities" | "v_ssh_keys";
  parentLabel: (row: any) => string;
  refreshToken?: number;
}> = ({ title, kind, shareResource, fkField, parentResource, parentLabel, refreshToken, }) => {
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open } = useNotification();
  const [modal, contextHolder] = Modal.useModal();

  const notifyError = (err: any, fallback = "Action failed") => {
    const msg = err?.message || fallback;
    const details = err?.body?.details || err?.body?.hint;
    if (open) open({ type: "error", message: msg, description: details });
    else message.error(msg);
  };

  const notifySuccess = (msg: string) => {
    if (open) open({ type: "success", message: msg });
    else message.success(msg);
  };

  const [loading, setLoading] = useState(false);

  // Lookups
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [parents, setParents] = useState<ParentRow[]>([]);

  // Raw shares from API
  const [shares, setShares] = useState<ShareRow[]>([]);

  // Filters
  const [search, setSearch] = useState("");
  const [filterGroupId, setFilterGroupId] = useState<string | undefined>(
    undefined,
  );
  const [filterParentId, setFilterParentId] = useState<string | undefined>(
    undefined,
  );
  const [onlyAdmin, setOnlyAdmin] = useState(false);
  const [onlyWritable, setOnlyWritable] = useState(false);

  const existingGroupIdsForSelectedParent = useMemo(() => {
    if (!filterParentId) return new Set<string>();
    const s = new Set<string>();
    for (const row of shares) {
      const pid = (row as any)[fkField] as string | undefined;
      if (pid === filterParentId) s.add(row.group_id);
    }
    return s;
  }, [shares, fkField, filterParentId]);

  // Add form
  const [addForm] = Form.useForm();

  // Bulk select
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const groupNameById = useMemo(
    () => new Map(groups.map((g) => [g.id, g.name])),
    [groups],
  );
  const parentLabelById = useMemo(
    () => new Map(parents.map((p) => [p.id, p.label])),
    [parents],
  );

  const clearFilters = () => {
    setSearch("");
    setFilterGroupId(undefined);
    setFilterParentId(undefined);
    setOnlyAdmin(false);
    setOnlyWritable(false);
    setSelectedRowKeys([]);
  };

  const loadLookups = React.useCallback(async () => {
    if (!dataProvider?.getList) return;

    const [gRes, pRes] = await Promise.all([
      dataProvider.getList({
        resource: "groups",
        pagination: { current: 1, pageSize: 5000 },
        sorters: [{ field: "name", order: "asc" }],
        meta: { profile: "iam" },
      }),
      dataProvider.getList({
        resource: parentResource,
        pagination: { current: 1, pageSize: 5000 },
        sorters:
          parentResource === "v_ssh_keys"
            ? [{ field: "updated_at", order: "desc" }]
            : [{ field: "updated_at", order: "desc" }],
        meta: { profile: "ssh" },
      }),
    ]);

    setGroups((gRes.data || []) as GroupRow[]);

    const parentRows = (pRes.data || []).map((r: any) => ({
      id: r.id,
      label: parentLabel(r),
    }));
    setParents(parentRows);
  }, [dataProvider, parentResource, parentLabel]);

  const loadShares = React.useCallback(async () => {
    if (!dataProvider?.getList) return;

    const filters: any[] = [];
    if (filterGroupId)
      filters.push({ field: "group_id", operator: "eq", value: filterGroupId });
    if (filterParentId)
      filters.push({ field: fkField, operator: "eq", value: filterParentId });

    const res = await dataProvider.getList({
      resource: shareResource,
      pagination: { current: 1, pageSize: 5000 },
      sorters: [{ field: "created_at", order: "desc" }],
      filters,
      meta: { profile: "ssh" },
    });

    setShares((res.data || []) as ShareRow[]);
  }, [dataProvider, shareResource, fkField, filterGroupId, filterParentId]);

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadLookups(), loadShares()]);
    } catch (e: any) {
      notifyError(e, "Failed to load shares");
    } finally {
      setLoading(false);
    }
  }, [loadLookups, loadShares]);


useEffect(() => {
  // if (!refreshToken) return;
  if (refreshToken == null) return;
  loadAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [refreshToken]);


  useEffect(() => {
    // When you change selected parent, clear chosen group to avoid "now hidden" selection
    addForm.setFieldsValue({ group_id: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParentId]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload shares when server-side filters change
  useEffect(() => {
    loadShares().catch(() => {});
  }, [filterGroupId, filterParentId, loadShares]);

  // Client-side refine
  const filteredShares = useMemo(() => {
    let rows = shares;

    if (onlyAdmin) rows = rows.filter((s) => !!s.can_admin);
    if (onlyWritable) rows = rows.filter((s) => isWritableShare(s));

    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((s) => {
      const g = groupNameById.get(s.group_id) || s.group_id;
      const pid = (s as any)[fkField] as string | undefined;
      const p = pid ? parentLabelById.get(pid) || pid : "";
      const blob = `${g} ${p}`.toLowerCase();
      return blob.includes(q);
    });
  }, [
    shares,
    onlyAdmin,
    onlyWritable,
    search,
    groupNameById,
    parentLabelById,
    fkField,
  ]);

  const addShare = async () => {
    try {
      const v = await addForm.validateFields();
      setLoading(true);
      const dup = shares.some((s) => {
        const pid = (s as any)[fkField];
        return pid === v.parent_id && s.group_id === v.group_id;
      });
      if (dup) {
        message.warning("That group is already shared to this item.");
        return;
      }

      await dataProvider.create({
        resource: shareResource,
        variables: {
          [fkField]: v.parent_id,
          group_id: v.group_id,
          can_read: true,
          can_create: !!v.can_create,
          can_update: !!v.can_update,
          can_delete: !!v.can_delete,
          can_admin: !!v.can_admin,
        },
        meta: { profile: "ssh" },
      });

      notifySuccess("Share added");
      addForm.resetFields([
        "group_id",
        "parent_id",
        "can_create",
        "can_update",
        "can_delete",
        "can_admin",
      ]);
      await loadShares();
    } catch (e: any) {
      notifyError(e, "Failed to add share");
    } finally {
      setLoading(false);
    }
  };

  const updateShare = async (row: ShareRow, patch: Partial<ShareRow>) => {
    try {
      setLoading(true);
      await dataProvider.update({
        resource: shareResource,
        id: row.id,
        variables: patch,
        meta: { profile: "ssh" },
      });
      await loadShares();
    } catch (e: any) {
      notifyError(e, "Failed to update share");
    } finally {
      setLoading(false);
    }
  };

  const removeShare = async (row: ShareRow) => {
    try {
      setLoading(true);
      await dataProvider.deleteOne({
        resource: shareResource,
        id: row.id,
        meta: { profile: "ssh" },
      });
      notifySuccess("Share removed");
      await loadShares();
    } catch (e: any) {
      notifyError(e, "Failed to remove share");
    } finally {
      setLoading(false);
    }
  };

  const bulkRemove = async () => {
    const ids = selectedRowKeys.map(String);
    if (!ids.length) return;

    modal.confirm({
      title: `Remove ${ids.length} share(s)?`,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoading(true);
        try {
          // sequential delete is simplest; if you want faster, you can add an RPC to bulk delete
          for (const id of ids) {
            await dataProvider.deleteOne({
              resource: shareResource,
              id,
              meta: { profile: "ssh" },
            });
          }
          setSelectedRowKeys([]);
          notifySuccess("Removed selected shares");
          await loadShares();
        } catch (e: any) {
          notifyError(e, "Bulk remove failed");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  /*
  const groupOptions = useMemo(
    () => groups.map((g) => ({ value: g.id, label: g.name })),
    [groups]
  );
*/

  const groupOptions = useMemo(() => {
    // If no parent selected, show all groups
    if (!filterParentId)
      return groups.map((g) => ({ value: g.id, label: g.name }));

    // If parent selected, hide groups already shared to it
    return groups
      .filter((g) => !existingGroupIdsForSelectedParent.has(g.id))
      .map((g) => ({ value: g.id, label: g.name }));
  }, [groups, filterParentId, existingGroupIdsForSelectedParent]);

  const parentOptions = useMemo(
    () => parents.map((p) => ({ value: p.id, label: p.label })),
    [parents],
  );

  return (
    <div>
      {contextHolder}

      <Card
        size="small"
        title={title}
        bodyStyle={{ padding: 12 }}
        style={{ marginBottom: 12 }}
      >
        <Space wrap>
          <Input
            allowClear
            size="small"
            placeholder="Search group / item..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240 }}
          />

          <Select
            allowClear
            size="small"
            style={{ width: 240 }}
            placeholder="Filter by group"
            value={filterGroupId}
            onChange={(v) => setFilterGroupId(v)}
            options={groupOptions}
            showSearch
            optionFilterProp="label"
          />

          <Select
            allowClear
            size="small"
            style={{ width: 360 }}
            placeholder={kind === "ca" ? "Filter by CA" : "Filter by Key"}
            value={filterParentId}
            onChange={(v) => setFilterParentId(v)}
            options={parentOptions}
            showSearch
            optionFilterProp="label"
          />

          <Checkbox
            checked={onlyAdmin}
            onChange={(e) => setOnlyAdmin(e.target.checked)}
          >
            Only admin
          </Checkbox>

          <Checkbox
            checked={onlyWritable}
            onChange={(e) => setOnlyWritable(e.target.checked)}
          >
            Only writable
          </Checkbox>

          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={loadAll}
            loading={loading}
          >
            Refresh
          </Button>

          <Button size="small" onClick={clearFilters} disabled={loading}>
            Clear filters
          </Button>

          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={!selectedRowKeys.length || loading}
            onClick={bulkRemove}
          >
            Remove selected ({selectedRowKeys.length})
          </Button>
        </Space>
      </Card>

      <Card
        size="small"
        title="Add Share"
        bodyStyle={{ padding: 12 }}
        style={{ marginBottom: 12 }}
      >
        <Form
          form={addForm}
          layout="inline"
          style={{ gap: 8, flexWrap: "wrap" }}
          initialValues={{
            can_create: false,
            can_update: false,
            can_delete: false,
            can_admin: false,
          }}
        >
          <Form.Item
            name="group_id"
            rules={[{ required: true, message: "Group required" }]}
          >
            <Select
              size="small"
              style={{ width: 240 }}
              placeholder={
                filterParentId ? "Select group" : "Select item first"
              }
              options={groupOptions}
              showSearch
              optionFilterProp="label"
              disabled={!filterParentId}
            />
          </Form.Item>

          <Form.Item
            name="parent_id"
            rules={[
              {
                required: true,
                message: kind === "ca" ? "CA required" : "Key required",
              },
            ]}
          >
            <Select
              size="small"
              style={{ width: 420 }}
              placeholder={kind === "ca" ? "Select CA" : "Select Key"}
              options={parentOptions}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item name="can_create" valuePropName="checked">
            <Checkbox>Create</Checkbox>
          </Form.Item>
          <Form.Item name="can_update" valuePropName="checked">
            <Checkbox>Update</Checkbox>
          </Form.Item>
          <Form.Item name="can_delete" valuePropName="checked">
            <Checkbox>Delete</Checkbox>
          </Form.Item>
          <Form.Item name="can_admin" valuePropName="checked">
            <Checkbox>Admin</Checkbox>
          </Form.Item>

          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={addShare}
            loading={loading}
          >
            Add
          </Button>
        </Form>

        <Divider style={{ margin: "10px 0" }} />
        <Text type="secondary">
          These shares grant access to a <b>group</b>. Group membership is
          controlled via <code>iam.group_shares</code>.
        </Text>
      </Card>

      <Table<ShareRow>
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={filteredShares}
        pagination={{ pageSize: 25 }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        columns={[
          {
            title: "Group",
            dataIndex: "group_id",
            width: 260,
            render: (gid: string) => groupNameById.get(gid) ?? gid,
          },
          {
            title: kind === "ca" ? "CA" : "Key",
            width: 520,
            render: (_, r) => {
              const pid = (r as any)[fkField] as string | undefined;
              if (!pid) return <Text type="secondary">—</Text>;
              return parentLabelById.get(pid) ?? pid;
            },
          },
          {
            title: "Create",
            dataIndex: "can_create",
            width: 90,
            render: (v: boolean, r) => (
              <Checkbox
                checked={!!v}
                onChange={(e) =>
                  updateShare(r, { can_create: e.target.checked })
                }
              />
            ),
          },
          {
            title: "Update",
            dataIndex: "can_update",
            width: 90,
            render: (v: boolean, r) => (
              <Checkbox
                checked={!!v}
                onChange={(e) =>
                  updateShare(r, { can_update: e.target.checked })
                }
              />
            ),
          },
          {
            title: "Delete",
            dataIndex: "can_delete",
            width: 90,
            render: (v: boolean, r) => (
              <Checkbox
                checked={!!v}
                onChange={(e) =>
                  updateShare(r, { can_delete: e.target.checked })
                }
              />
            ),
          },
          {
            title: "Admin",
            dataIndex: "can_admin",
            width: 90,
            render: (v: boolean, r) => (
              <Checkbox
                checked={!!v}
                onChange={(e) =>
                  updateShare(r, { can_admin: e.target.checked })
                }
              />
            ),
          },
          {
            title: "",
            width: 70,
            render: (_, r) => (
              <Popconfirm
                title="Remove share?"
                onConfirm={() => removeShare(r)}
                okText="Remove"
                cancelText="Cancel"
              >
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            ),
          },
        ]}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

export const SSHCertControl: React.FC = () => {
  const isMobile = useIsMobile();
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open } = useNotification();
  const [modal, contextHolder] = Modal.useModal();
  const { token } = theme.useToken();

  const notifySuccess = (msg: string) => {
    if (open) open({ type: "success", message: msg });
    else message.success(msg);
  };

  const notifyError = (err: any, fallback = "Action failed") => {
    const msg = err?.message || fallback;
    const details = err?.body?.details || err?.body?.hint;
    if (open) open({ type: "error", message: msg, description: details });
    else message.error(msg);
  };

  const [activeTab, setActiveTab] = useState<
    "certs" | "cas" | "keys" | "issue" | "ca_shares" | "key_shares"
  >("certs");

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Data
  const [cas, setCas] = useState<CaRow[]>([]);
  const [keys, setKeys] = useState<KeyRow[]>([]);
  const [certs, setCerts] = useState<CertRow[]>([]);

  // Filters
  const [filterCertType, setFilterCertType] = useState<"" | "host" | "user">(
    "",
  );
  const [filterCertStatus, setFilterCertStatus] = useState<
    "" | "pending" | "issued" | "revoked" | "expired"
  >("");

  // --- Loaders ---
  const loadCAs = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    const res = await dataProvider.getList({
      resource: "v_ssh_certificate_authorities",
      pagination: { current: 1, pageSize: 500 },
      sorters: [{ field: "updated_at", order: "desc" }],
      meta: { profile: SSH_SCHEMA },
    });
    setCas((res.data || []) as CaRow[]);
  }, [dataProvider]);

  const loadKeys = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    const res = await dataProvider.getList({
      resource: "v_ssh_keys",
      pagination: { current: 1, pageSize: 2000 },
      sorters: [{ field: "updated_at", order: "desc" }],
      meta: { profile: SSH_SCHEMA },
    });
    setKeys((res.data || []) as KeyRow[]);
  }, [dataProvider]);

  const loadCerts = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    const filters: any[] = [];
    if (filterCertType)
      filters.push({
        field: "cert_type",
        operator: "eq",
        value: filterCertType,
      });
    if (filterCertStatus)
      filters.push({
        field: "status",
        operator: "eq",
        value: filterCertStatus,
      });

    const res = await dataProvider.getList({
      resource: "v_ssh_certificates",
      pagination: { current: 1, pageSize: 2000 },
      sorters: [{ field: "created_at", order: "desc" }],
      filters,
      meta: { profile: SSH_SCHEMA },
    });
    setCerts((res.data || []) as CertRow[]);
  }, [dataProvider, filterCertType, filterCertStatus]);

  const loadAll = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    setLoading(true);
    try {
      await Promise.all([loadCAs(), loadKeys(), loadCerts()]);
    } catch (e: any) {
      notifyError(e, "Failed to load SSH PKI data");
    } finally {
      setLoading(false);
    }
  }, [dataProvider, loadCAs, loadKeys, loadCerts]);

  const realtimeTopics = React.useMemo(() => {
    return [
      "ssh.ssh_certificate_authorities",
      "ssh.ssh_keys",
      "ssh.ssh_certificates",
      "ssh.ssh_key_shares",
      "ssh.ssh_ca_shares", // or underlying table name if this is a view
    ];
  }, []);

  usePGBroadcastRefresh({
    topics: realtimeTopics,
    debounceMs: 200,
    enabled: true,
    onChange: () => {
      setRefreshKey((k) => k + 1);
    },
  });


  useEffect(() => {
    loadAll();
  }, [loadAll, refreshKey]);

  useEffect(() => {
    // Cert filters should refresh cert list only
    loadCerts().catch(() => {});
  }, [filterCertType, filterCertStatus, loadCerts]);

  const reloadCurrent = async () => {
    setRefreshKey((k) => k + 1);
  };

  // --- Derived maps / options ---
  const caById = useMemo(() => new Map(cas.map((c) => [c.id, c])), [cas]);
  const keyById = useMemo(() => new Map(keys.map((k) => [k.id, k])), [keys]);

  const caOptions = useMemo(
    () =>
      cas.map((c) => ({
        label: `${c.name} (${c.ca_type}, ${c.status})`,
        value: c.id,
      })),
    [cas],
  );

  const keyOptions = useMemo(
    () =>
      keys.map((k) => ({
        label: `${k.identity} • ${k.name} (${k.key_type})`,
        value: k.id,
      })),
    [keys],
  );

  // --- Search ---
  const filteredCAs = filterRows(cas, search);
  const filteredKeys = filterRows(keys, search);
  const filteredCerts = filterRows(certs, search);

  // --- Overlays state ---
  const [caOverlayOpen, setCaOverlayOpen] = useState(false);
  const [caMode, setCaMode] = useState<"view" | "edit">("edit");
  const [activeCa, setActiveCa] = useState<any | null>(null);

  const [keyOverlayOpen, setKeyOverlayOpen] = useState(false);
  const [keyMode, setKeyMode] = useState<"view" | "edit">("edit");
  const [activeKey, setActiveKey] = useState<any | null>(null);

  const [certOverlayOpen, setCertOverlayOpen] = useState(false);
  const [certMode, setCertMode] = useState<"view" | "edit">("view");
  const [activeCert, setActiveCert] = useState<any | null>(null);

  const [issueOverlayOpen, setIssueOverlayOpen] = useState(false);

  // Forms
  const [caForm] = Form.useForm();
  const [keyForm] = Form.useForm();
  const [certForm] = Form.useForm();
  const [issueForm] = Form.useForm();

  // --- Openers ---
  const openCa = (rec: any, mode: "view" | "edit") => {
    setActiveCa(rec);
    setCaMode(mode);
    caForm.setFieldsValue({
      name: rec?.name ?? "",
      ca_type: rec?.ca_type ?? "host",
      key_type: rec?.key_type ?? "ed25519",
      status: rec?.status ?? "active",
      note: rec?.note ?? "",
      signing_enabled: rec?.signing_enabled ?? false,
      signing_mode: rec?.signing_mode ?? "manual",
    });
    setCaOverlayOpen(true);
  };

  const openKey = (rec: any, mode: "view" | "edit") => {
    setActiveKey(rec);
    setKeyMode(mode);
    keyForm.setFieldsValue({
      identity: rec?.identity ?? "",
      name: rec?.name ?? "",
      key_type: rec?.key_type ?? "ed25519",
      note: rec?.note ?? "",
    });
    setKeyOverlayOpen(true);
  };

  const openCert = (rec: any, mode: "view" | "edit") => {
    setActiveCert(rec);
    setCertMode(mode);
    certForm.setFieldsValue({
      note: rec?.note ?? "",
      status: rec?.status ?? "issued",
      cert_public_key: rec?.cert_public_key ?? "",
      cert_fingerprint: rec?.cert_fingerprint ?? "",
    });
    setCertOverlayOpen(true);
  };

  const openIssue = (seed?: Partial<any>) => {
    issueForm.setFieldsValue({
      ca_id: seed?.ca_id ?? undefined,
      subject_key_id: seed?.subject_key_id ?? undefined,
      cert_type: seed?.cert_type ?? "host",
      identity: seed?.identity ?? "",
      principals: seed?.principals?.join(", ") ?? "",
      key_id: seed?.key_id ?? "",
      valid_from: seed?.valid_after ? dayjs(seed.valid_after) : dayjs(),
      validity: seed?.validity ?? "1 year",
      note: seed?.note ?? "",
      critical_options_json: seed?.critical_options_json ?? "{}",
      extensions_json: seed?.extensions_json ?? "{}",
    });
    setIssueOverlayOpen(true);
  };

  // --- Save handlers (CAs/Keys mostly via RPC; cert updates are DB updates) ---
  const handleSaveCA = async () => {
    if (!activeCa) return;
    try {
      const vals = await caForm.validateFields();
      const vars = {
        p_name: vals.name,
        p_ca_type: vals.ca_type,
        p_key_type: vals.key_type,
      };

      if (activeCa.id === "__new__") {
        // Uses your DB function: ssh.create_ssh_certificate_authority(name, ca_type, key_type)
        await dataProvider.custom({
          url: `/rpc/create_ssh_certificate_authority`,
          method: "POST",
          data: vars,
          meta: { profile: SSH_SCHEMA },
        });
        notifySuccess("CA created");
      } else {
        // Simple CA metadata update via table update (status/note/key_type)
        await dataProvider.update({
          resource: "ssh_certificate_authorities",
          id: activeCa.id,
          variables: {
            status: vals.status,
            note: vals.note,
            key_type: vals.key_type,
            signing_enabled: !!vals.signing_enabled,
            signing_mode: vals.signing_mode || "manual",
          },
          meta: { profile: SSH_SCHEMA },
        });
        notifySuccess("CA updated");
      }

      setCaOverlayOpen(false);
      setActiveCa(null);
      await loadAll();
    } catch (e: any) {
      notifyError(e);
    }
  };

  const handleSaveKey = async () => {
    if (!activeKey) return;
    try {
      const vals = await keyForm.validateFields();
      if (activeKey.id === "__new__") {
        // Uses DB function: ssh.create_ssh_keypair(identity, name, key_type, note)
        await dataProvider.custom({
          url: `/rpc/create_ssh_keypair`,
          method: "POST",
          data: {
            p_identity: vals.identity,
            p_name: vals.name,
            p_key_type: vals.key_type,
            p_note: vals.note || null,
          },
          meta: { profile: SSH_SCHEMA },
        });
        notifySuccess("Keypair created");
      } else {
        // Only allow updating metadata (not keys) in UI
        await dataProvider.update({
          resource: "ssh_keys",
          id: activeKey.id,
          variables: {
            identity: vals.identity,
            name: vals.name,
            note: vals.note || null,
          },
          meta: { profile: SSH_SCHEMA },
        });
        notifySuccess("Key metadata updated");
      }

      setKeyOverlayOpen(false);
      setActiveKey(null);
      await loadAll();
    } catch (e: any) {
      notifyError(e);
    }
  };

  const handleSaveCert = async () => {
    if (!activeCert) return;
    try {
      const vals = await certForm.validateFields();

      // Prefer: use RPC that marks it issued and sets cert text/fingerprint
      // - If user edits cert_public_key manually, treat it as "signed"
      // - Otherwise allow status/note changes
      if (vals.cert_public_key && vals.cert_public_key !== "PENDING") {
        await dataProvider.custom({
          url: `/rpc/set_ssh_certificate_signed`,
          method: "POST",
          data: {
            p_cert_id: activeCert.id,
            p_cert_public_key: vals.cert_public_key,
            p_cert_fingerprint: vals.cert_fingerprint || null,
          },
          meta: { profile: SSH_SCHEMA },
        });
        notifySuccess("Certificate marked as issued");
      } else {
        await dataProvider.update({
          resource: "ssh_certificates",
          id: activeCert.id,
          variables: { status: vals.status, note: vals.note || null },
          meta: { profile: SSH_SCHEMA },
        });
        notifySuccess("Certificate updated");
      }

      setCertOverlayOpen(false);
      setActiveCert(null);
      await loadAll();
    } catch (e: any) {
      notifyError(e);
    }
  };

  const handleIssue = async () => {
    try {
      const vals = await issueForm.validateFields();

      const principals = (vals.principals || "")
        .split(",")
        .map((s: string) => s.trim())
        .filter(Boolean);

      const validFrom = vals.valid_from
        ? dayjs(vals.valid_from).toISOString()
        : dayjs().toISOString();

      // validity is an interval string (e.g. "60 days", "1 year", "8 hours")
      // your SQL function expects an INTERVAL; PostgREST will pass as text and PG will cast
      const payload = {
        p_ca_id: vals.ca_id,
        p_subject_key_id: vals.subject_key_id,
        p_cert_type: vals.cert_type,
        p_identity: vals.identity,
        p_principals: principals,
        p_key_id: vals.key_id || null,
        p_valid_from: validFrom,
        p_validity: vals.validity || "1 year",
        p_critical_options: JSON.parse(vals.critical_options_json || "{}"),
        p_extensions: JSON.parse(vals.extensions_json || "{}"),
        p_note: vals.note || null,
      };

      await dataProvider.custom({
        url: `/rpc/create_ssh_certificate_request`,
        method: "POST",
        data: payload,
        meta: { profile: SSH_SCHEMA },
      });

      notifySuccess("Certificate request created (pending)");
      setIssueOverlayOpen(false);
      await loadAll();
    } catch (e: any) {
      notifyError(e, "Failed to create certificate request");
    }
  };

  const handleRevoke = (certId: string, label: string) => {
    modal.confirm({
      title: `Revoke certificate?`,
      content: label,
      okText: "Revoke",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await dataProvider.custom({
            url: `/rpc/revoke_ssh_certificate`,
            method: "POST",
            data: { p_cert_id: certId, p_reason: "revoked via UI" },
            meta: { profile: SSH_SCHEMA },
          });
          notifySuccess("Revoked");
          await loadAll();
        } catch (e: any) {
          notifyError(e, "Failed to revoke");
        }
      },
    });
  };

  const handleDelete = (resource: string, id: string, label: string) => {
    modal.confirm({
      title: `Delete ${label}?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await dataProvider.deleteOne({
            resource,
            id,
            meta: { profile: SSH_SCHEMA },
          });
          notifySuccess("Deleted");
          await loadAll();
        } catch (e: any) {
          notifyError(e);
        }
      },
    });
  };

  // --- Table scroll helper ---
  const getTableScroll = (width: number) => {
    if (isMobile && !UI.ENABLE_MOBILE_TABLE_SCROLL) return undefined;
    return { x: width };
  };

  // --- Render ---
  return (
    <div style={{ minWidth: UI.PAGE_MIN_WIDTH_PX, padding: 16 }}>
      {contextHolder}

      {/* Title */}
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: 20, lineHeight: 1.2 }}>
          SSH Certificates
        </Text>
        <div style={{ marginTop: 4 }}>
          <Text type="secondary">
            Manage SSH CAs, keypairs, and certificate requests/issuance. Not
            X.509.{" "}
            <a href="https://docs.rockylinux.org/10/ko/guides/security/ssh_ca_key_signing/">
              SSH CA Doc
            </a>
          </Text>
        </div>
      </div>

      {/* Controls */}
      <div style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            allowClear
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240, maxWidth: UI.SEARCH_MAX_WIDTH_PX }}
            suffix={
              search ? (
                <CloseCircleOutlined
                  onClick={() => setSearch("")}
                  style={{ cursor: "pointer" }}
                />
              ) : null
            }
          />

          {activeTab === "certs" && (
            <>
              <Select
                size="small"
                style={{ width: 140 }}
                value={filterCertType}
                onChange={(v) => setFilterCertType(v)}
                options={[
                  { label: "All types", value: "" },
                  { label: "Host", value: "host" },
                  { label: "User", value: "user" },
                ]}
              />
              <Select
                size="small"
                style={{ width: 160 }}
                value={filterCertStatus}
                onChange={(v) => setFilterCertStatus(v)}
                options={[
                  { label: "All status", value: "" },
                  { label: "pending", value: "pending" },
                  { label: "issued", value: "issued" },
                  { label: "revoked", value: "revoked" },
                  { label: "expired", value: "expired" },
                ]}
              />
            </>
          )}

          <Tooltip title="Refresh">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={reloadCurrent}
              loading={loading}
            />
          </Tooltip>

          <Tooltip title="New cert request">
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openIssue()}
            >
              Issue
            </Button>
          </Tooltip>
        </Space>
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={(k) => setActiveTab(k as any)}
        style={{ overflowX: UI.ENABLE_TAB_SCROLL ? "auto" : "visible" }}
        items={[
          { key: "certs", label: "Certificates", icon: <IdcardOutlined /> },
          { key: "cas", label: "CAs", icon: <SafetyOutlined /> },
          { key: "keys", label: "Keys", icon: <KeyOutlined /> },
          { key: "issue", label: "Issue", icon: <PlusOutlined /> },
          { key: "ca_shares", label: "CA Shares", icon: <SafetyOutlined /> },
          { key: "key_shares", label: "Key Shares", icon: <KeyOutlined /> },
        ]}
      />

      {/* CA Shares (global) */}
      {activeTab === "ca_shares" && (
        <SharesBrowser
          title="CA Shares"
          kind="ca"
          shareResource="v_ssh_ca_shares"
          fkField="ca_id"
          parentResource="v_ssh_certificate_authorities"
          parentLabel={(r: any) => `${r.name} (${r.ca_type}, ${r.status})`}
          refreshToken={refreshKey}
        />
      )}

      {/* Key Shares (global) */}
      {activeTab === "key_shares" && (
        <SharesBrowser
          title="Key Shares"
          kind="key"
          shareResource="ssh_key_shares"
          fkField="key_id"
          parentResource="v_ssh_keys"
          parentLabel={(r: any) => `${r.identity} • ${r.name} (${r.key_type})`}
          refreshToken={refreshKey}
        />
      )}

      {/* Certificates */}
      {activeTab === "certs" && (
        <>
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={filteredCerts}
            scroll={getTableScroll(1700)}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: "",
                width: 44,
                fixed: "left",
                render: (_, r: CertRow) => (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "view",
                          icon: <EyeOutlined />,
                          label: "View",
                          onClick: () => openCert(r, "view"),
                        },
                        {
                          key: "issue-like",
                          icon: <PlusOutlined />,
                          label: "Issue another like this",
                          onClick: () =>
                            openIssue({
                              ca_id: r.ca_id,
                              subject_key_id: r.subject_key_id,
                              cert_type: r.cert_type,
                              identity: r.identity,
                              principals: r.principals,
                              note: r.note,
                            }),
                        },
                        {
                          key: "copy",
                          icon: <CopyOutlined />,
                          label: "Copy cert",
                          onClick: () => copyToClipboard(r.cert_public_key),
                        },
                        {
                          key: "sign",
                          icon: <SafetyOutlined />,
                          label: "Sign now",
                          disabled: r.status !== "pending",
                          onClick: async () => {
                            try {
                              await dataProvider.custom({
                                url: `/rpc/sign_ssh_certificate`,
                                method: "POST",
                                data: { p_cert_id: r.id },
                                meta: { profile: SSH_SCHEMA },
                              });
                              notifySuccess("Signed");
                              await loadCerts();
                            } catch (e: any) {
                              notifyError(e, "Failed to sign");
                            }
                          },
                        },

                        {
                          key: "revoke",
                          icon: <StopOutlined style={{ color: "red" }} />,
                          label: <span style={{ color: "red" }}>Revoke</span>,
                          disabled: r.status === "revoked",
                          onClick: () =>
                            handleRevoke(
                              r.id,
                              `${r.identity} (serial ${r.serial})`,
                            ),
                        },
                        { type: "divider" },
                        {
                          key: "del",
                          icon: <DeleteOutlined style={{ color: "red" }} />,
                          label: <span style={{ color: "red" }}>Delete</span>,
                          onClick: () =>
                            handleDelete(
                              "ssh_certificates",
                              r.id,
                              "certificate",
                            ),
                        },
                      ],
                    }}
                  >
                    <Button size="small" type="text" icon={<MoreOutlined />} />
                  </Dropdown>
                ),
              },
              {
                title: "Identity",
                dataIndex: "identity",
                width: 260,
                render: (v, r) => (
                  <Space size={6} wrap>
                    <Text strong>{v}</Text>
                    <Tag color={r.cert_type === "host" ? "blue" : "purple"}>
                      {r.cert_type}
                    </Tag>
                  </Space>
                ),
              },
              {
                title: "CA",
                dataIndex: "ca_id",
                width: 260,
                render: (id: string) => {
                  const ca = caById.get(id);
                  return ca ? (
                    <Space size={6} wrap>
                      <Text>{ca.name}</Text>
                      <Tag>{ca.ca_type}</Tag>
                      <Tag color={ca.status === "active" ? "green" : "default"}>
                        {ca.status}
                      </Tag>
                    </Space>
                  ) : (
                    <Text type="secondary">{id}</Text>
                  );
                },
              },
              {
                title: "Key",
                dataIndex: "subject_key_id",
                width: 300,
                render: (id: string) => {
                  const k = keyById.get(id);
                  return k ? (
                    <Space direction="vertical" size={0}>
                      <Text>{k.identity}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {k.name} • {k.key_type}
                      </Text>
                    </Space>
                  ) : (
                    <Text type="secondary">{id}</Text>
                  );
                },
              },
              {
                title: "Principals",
                dataIndex: "principals",
                width: 320,
                render: (arr: string[]) =>
                  arr?.length ? (
                    <Space size={4} wrap>
                      {arr.slice(0, 6).map((p) => (
                        <Tag key={p}>{p}</Tag>
                      ))}
                      {arr.length > 6 && <Tag>+{arr.length - 6}</Tag>}
                    </Space>
                  ) : (
                    <Text type="secondary">—</Text>
                  ),
              },
              { title: "Serial", dataIndex: "serial", width: 90 },
              {
                title: "Status",
                dataIndex: "status",
                width: 110,
                render: (v) => statusTag(v),
              },
              {
                title: "Validity",
                width: 110,
                render: (_, r) => validityTag(r),
              },
              {
                title: "Valid From",
                dataIndex: "valid_after",
                width: 170,
                render: fmtTime,
              },
              {
                title: "Valid To",
                dataIndex: "valid_before",
                width: 170,
                render: fmtTime,
              },
              {
                title: "Cert",
                width: 90,
                render: (_, r) => (
                  <Button
                    size="small"
                    icon={<FileTextOutlined />}
                    onClick={() => openCert(r, "view")}
                  />
                ),
              },
            ]}
          />
        </>
      )}

      {/* CAs */}
      {activeTab === "cas" && (
        <>
          <div style={{ marginBottom: 8 }}>
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openCa({ id: "__new__" }, "edit")}
            >
              New CA
            </Button>
          </div>

          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={filteredCAs}
            scroll={getTableScroll(1400)}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: "",
                width: 44,
                fixed: "left",
                render: (_, r: CaRow) => (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "view",
                          icon: <EyeOutlined />,
                          label: "View",
                          onClick: () => openCa(r, "view"),
                        },
                        {
                          key: "edit",
                          icon: <EditOutlined />,
                          label: "Edit",
                          onClick: () => openCa(r, "edit"),
                        },
                        { type: "divider" },
                        {
                          key: "copy",
                          icon: <CopyOutlined />,
                          label: "Copy public key",
                          onClick: () => copyToClipboard(r.ca_public_key),
                        },
                        {
                          key: "del",
                          icon: <DeleteOutlined style={{ color: "red" }} />,
                          label: <span style={{ color: "red" }}>Delete</span>,
                          onClick: () =>
                            handleDelete(
                              "ssh_certificate_authorities",
                              r.id,
                              "CA",
                            ),
                        },
                      ],
                    }}
                  >
                    <Button size="small" type="text" icon={<MoreOutlined />} />
                  </Dropdown>
                ),
              },
              {
                title: "Name",
                dataIndex: "name",
                width: 320,
                render: (v) => <Text strong>{v}</Text>,
              },
              {
                title: "Type",
                dataIndex: "ca_type",
                width: 100,
                render: (v) => (
                  <Tag color={v === "host" ? "blue" : "purple"}>{v}</Tag>
                ),
              },
              { title: "Key Type", dataIndex: "key_type", width: 140 },
              {
                title: "Status",
                dataIndex: "status",
                width: 140,
                render: (v) => (
                  <Tag color={v === "active" ? "green" : "default"}>{v}</Tag>
                ),
              },
              {
                title: "Fingerprint",
                dataIndex: "ca_public_fingerprint",
                width: 260,
                render: (v) => v || <Text type="secondary">—</Text>,
              },
              {
                title: "Public Key",
                dataIndex: "ca_public_key",
                width: 520,
                render: (v) => (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Text code ellipsis style={{ maxWidth: 460 }}>
                      {v}
                    </Text>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(v)}
                    />
                  </div>
                ),
              },
            ]}
          />
        </>
      )}

      {/* Keys */}
      {activeTab === "keys" && (
        <>
          <div style={{ marginBottom: 8 }}>
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openKey({ id: "__new__" }, "edit")}
            >
              New Keypair
            </Button>
          </div>

          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={filteredKeys}
            scroll={getTableScroll(1500)}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: "",
                width: 44,
                fixed: "left",
                render: (_, r: KeyRow) => (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "view",
                          icon: <EyeOutlined />,
                          label: "View",
                          onClick: () => openKey(r, "view"),
                        },
                        {
                          key: "edit",
                          icon: <EditOutlined />,
                          label: "Edit",
                          onClick: () => openKey(r, "edit"),
                        },
                        {
                          key: "issue-host",
                          icon: <PlusOutlined />,
                          label: "Issue host cert",
                          onClick: () =>
                            openIssue({
                              subject_key_id: r.id,
                              cert_type: "host",
                              identity: r.identity,
                              principals: [r.identity],
                            }),
                        },
                        {
                          key: "issue-user",
                          icon: <PlusOutlined />,
                          label: "Issue user cert",
                          onClick: () =>
                            openIssue({
                              subject_key_id: r.id,
                              cert_type: "user",
                              identity: r.identity,
                              principals: [r.identity],
                            }),
                        },
                        { type: "divider" },
                        {
                          key: "copy",
                          icon: <CopyOutlined />,
                          label: "Copy public key",
                          onClick: () => copyToClipboard(r.public_key),
                        },
                        {
                          key: "del",
                          icon: <DeleteOutlined style={{ color: "red" }} />,
                          label: <span style={{ color: "red" }}>Delete</span>,
                          onClick: () => handleDelete("ssh_keys", r.id, "key"),
                        },
                      ],
                    }}
                  >
                    <Button size="small" type="text" icon={<MoreOutlined />} />
                  </Dropdown>
                ),
              },
              {
                title: "Identity",
                dataIndex: "identity",
                width: 260,
                render: (v) => <Text strong>{v}</Text>,
              },
              { title: "Name", dataIndex: "name", width: 240 },
              { title: "Key Type", dataIndex: "key_type", width: 120 },
              {
                title: "Fingerprint",
                dataIndex: "public_key_fingerprint",
                width: 240,
                render: (v) => v || <Text type="secondary">—</Text>,
              },
              {
                title: "Public Key",
                dataIndex: "public_key",
                width: 520,
                render: (v) => (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Text code ellipsis style={{ maxWidth: 460 }}>
                      {v}
                    </Text>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(v)}
                    />
                  </div>
                ),
              },
              {
                title: "Note",
                dataIndex: "note",
                width: 260,
                render: (v) => v || <Text type="secondary">—</Text>,
              },
            ]}
          />
        </>
      )}

      {/* Issue (quick form) */}
      {activeTab === "issue" && (
        <div
          style={{
            padding: 12,
            border: `1px solid ${token.colorBorder}`,
            borderRadius: token.borderRadiusLG,
            background: token.colorBgContainer,
            maxWidth: 920,
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <Text strong style={{ fontSize: 16 }}>
              Create Certificate Request
            </Text>
            <div>
              <Text type="secondary">
                Creates a DB request row using
                ssh.create_ssh_certificate_request (defaults to 1 year).
              </Text>
            </div>
          </div>

          <Divider style={{ margin: "8px 0" }} />

          <Form form={issueForm} layout="vertical">
            <Flex gap={12} wrap>
              <Form.Item
                name="cert_type"
                label="Cert Type"
                rules={[{ required: true }]}
                style={{ minWidth: 180, flex: "1 1 180px" }}
              >
                <Select
                  options={[
                    { label: "Host", value: "host" },
                    { label: "User", value: "user" },
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="ca_id"
                label="CA"
                rules={[{ required: true }]}
                style={{ minWidth: 320, flex: "2 1 320px" }}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={caOptions}
                  placeholder="Select CA"
                />
              </Form.Item>
            </Flex>

            <Form.Item
              name="subject_key_id"
              label="Subject Key"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={keyOptions}
                placeholder="Select key to certify"
              />
            </Form.Item>

            <Flex gap={12} wrap>
              <Form.Item
                name="identity"
                label="Identity"
                rules={[{ required: true }]}
                style={{ minWidth: 260, flex: "2 1 260px" }}
              >
                <Input placeholder="web01.example.com or alice" />
              </Form.Item>

              <Form.Item
                name="key_id"
                label="Key ID (optional)"
                style={{ minWidth: 240, flex: "2 1 240px" }}
              >
                <Input placeholder="host:web01.example.com:123" />
              </Form.Item>
            </Flex>

            <Form.Item
              name="principals"
              label="Principals (comma-separated)"
              tooltip="OpenSSH principals. Host certs usually include FQDN and shortname. User certs include login names."
            >
              <Input placeholder="web01.example.com, web01" />
            </Form.Item>

            <Flex gap={12} wrap>
              <Form.Item
                name="valid_from"
                label="Valid From"
                style={{ minWidth: 240, flex: "1 1 240px" }}
                initialValue={dayjs()}
              >
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="validity"
                label="Validity (interval)"
                tooltip='Any PostgreSQL interval string. Examples: "8 hours", "30 days", "1 year". Default is 1 year.'
                style={{ minWidth: 240, flex: "1 1 240px" }}
                initialValue="1 year"
              >
                <Input placeholder="1 year" />
              </Form.Item>
            </Flex>

            <Flex gap={12} wrap>
              <Form.Item
                name="critical_options_json"
                label="Critical Options (JSON)"
                style={{ minWidth: 300, flex: "1 1 300px" }}
                initialValue="{}"
              >
                <TextArea rows={4} style={{ fontFamily: "monospace" }} />
              </Form.Item>

              <Form.Item
                name="extensions_json"
                label="Extensions (JSON)"
                style={{ minWidth: 300, flex: "1 1 300px" }}
                initialValue="{}"
              >
                <TextArea rows={4} style={{ fontFamily: "monospace" }} />
              </Form.Item>
            </Flex>

            <Form.Item name="note" label="Note">
              <Input />
            </Form.Item>

            <Space>
              <Button
                type="primary"
                onClick={handleIssue}
                icon={<PlusOutlined />}
              >
                Create Request
              </Button>
              <Button onClick={() => issueForm.resetFields()}>Reset</Button>
            </Space>
          </Form>
        </div>
      )}

      {/* --- Overlays --- */}

      {/* CA Overlay */}
      <ResponsiveEditor
        open={caOverlayOpen}
        onClose={() => setCaOverlayOpen(false)}
        title={
          caMode === "view"
            ? "View CA"
            : activeCa?.id === "__new__"
              ? "New CA"
              : "Edit CA"
        }
        onOk={caMode === "edit" ? handleSaveCA : undefined}
        footer={caMode === "view" ? null : undefined}
        width={UI.MODAL_DESKTOP_WIDTH_PX}
      >
        <Form form={caForm} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input readOnly={caMode === "view" || activeCa?.id !== "__new__"} />
          </Form.Item>

          <Flex gap={12}>
            <Form.Item
              name="ca_type"
              label="CA Type"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <Select
                disabled={caMode === "view" || activeCa?.id !== "__new__"}
                options={[
                  { label: "Host", value: "host" },
                  { label: "User", value: "user" },
                ]}
              />
            </Form.Item>

            <Form.Item
              name="key_type"
              label="Key Type"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <Select
                disabled={caMode === "view" || activeCa?.id !== "__new__"}
                options={[
                  { label: "ed25519", value: "ed25519" },
                  { label: "rsa3072", value: "rsa3072" },
                  { label: "rsa4096", value: "rsa4096" },
                ]}
              />
            </Form.Item>
          </Flex>

          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              disabled={caMode === "view" || activeCa?.id === "__new__"}
              options={[
                { label: "active", value: "active" },
                { label: "inactive", value: "inactive" },
                { label: "retired", value: "retired" },
                { label: "compromised", value: "compromised" },
              ]}
            />
          </Form.Item>

          <Form.Item name="note" label="Note">
            <Input readOnly={caMode === "view"} />
          </Form.Item>

          {activeCa?.id !== "__new__" && (
            <>
              <Divider style={{ margin: "8px 0" }} />
              <Text type="secondary">
                Public key shown in the table. Private key remains encrypted
                server-side.
              </Text>
            </>
          )}

          <Divider style={{ margin: "8px 0" }} />

          <Flex gap={12} wrap>
            <Form.Item
              name="signing_enabled"
              label="Auto-sign enabled"
              valuePropName="checked"
              style={{ minWidth: 220, flex: "0 0 220px" }}
            >
              <Checkbox disabled={caMode === "view"} />
            </Form.Item>

            <Form.Item
              name="signing_mode"
              label="Signing mode"
              style={{ minWidth: 240, flex: "1 1 240px" }}
              tooltip="manual = create requests as pending; auto_on_request = sign immediately when a request is created"
            >
              <Select
                disabled={caMode === "view"}
                options={[
                  { label: "manual", value: "manual" },
                  { label: "auto_on_request", value: "auto_on_request" },
                ]}
              />
            </Form.Item>
          </Flex>

          <Text type="secondary">
            Auto-sign requires <Text code>ssh.sign_ssh_certificate</Text> to be
            available and <Text code>ssh-keygen</Text> installed on the DB host.
          </Text>
        </Form>
      </ResponsiveEditor>

      {/* Key Overlay */}
      <ResponsiveEditor
        open={keyOverlayOpen}
        onClose={() => setKeyOverlayOpen(false)}
        title={
          keyMode === "view"
            ? "View Key"
            : activeKey?.id === "__new__"
              ? "New Keypair"
              : "Edit Key"
        }
        onOk={keyMode === "edit" ? handleSaveKey : undefined}
        footer={keyMode === "view" ? null : undefined}
        width={UI.MODAL_DESKTOP_WIDTH_PX}
      >
        <Form form={keyForm} layout="vertical">
          <Form.Item
            name="identity"
            label="Identity"
            rules={[{ required: true }]}
          >
            <Input
              readOnly={keyMode === "view"}
              placeholder="web01.example.com or alice"
            />
          </Form.Item>

          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input
              readOnly={keyMode === "view"}
              placeholder="ssh_host_ed25519"
            />
          </Form.Item>

          <Form.Item
            name="key_type"
            label="Key Type"
            rules={[{ required: true }]}
          >
            <Select
              disabled={keyMode === "view" || activeKey?.id !== "__new__"}
              options={[
                { label: "ed25519", value: "ed25519" },
                { label: "rsa3072", value: "rsa3072" },
                { label: "rsa4096", value: "rsa4096" },
              ]}
            />
          </Form.Item>

          <Form.Item name="note" label="Note">
            <Input readOnly={keyMode === "view"} />
          </Form.Item>

          {activeKey?.id !== "__new__" && (
            <>
              <Divider style={{ margin: "8px 0" }} />
              <Text type="secondary">
                This UI does not expose encrypted private keys. To rotate keys,
                create a new keypair and re-issue certs.
              </Text>
            </>
          )}
        </Form>
      </ResponsiveEditor>

      {/* Cert Overlay */}
      <ResponsiveEditor
        open={certOverlayOpen}
        onClose={() => setCertOverlayOpen(false)}
        title={certMode === "view" ? "View Certificate" : "Edit Certificate"}
        onOk={certMode === "edit" ? handleSaveCert : undefined}
        footer={certMode === "view" ? null : undefined}
        width={UI.MODAL_WIDE_WIDTH_PX}
      >
        {activeCert && (
          <>
            <div style={{ marginBottom: 8 }}>
              <Space wrap>
                <Tag
                  color={activeCert.cert_type === "host" ? "blue" : "purple"}
                >
                  {activeCert.cert_type}
                </Tag>
                {statusTag(activeCert.status)}
                {validityTag(activeCert)}
                <Tag>
                  Serial <b>{activeCert.serial}</b>
                </Tag>
                <Tag>
                  Key ID <b>{activeCert.key_id}</b>
                </Tag>
              </Space>
            </div>

            <Divider style={{ margin: "8px 0" }} />

            <Form form={certForm} layout="vertical">
              <Form.Item name="note" label="Note">
                <Input readOnly={certMode === "view"} />
              </Form.Item>

              <Flex gap={12} wrap>
                <Form.Item
                  name="status"
                  label="Status"
                  style={{ minWidth: 200, flex: "0 0 200px" }}
                >
                  <Select
                    disabled={certMode === "view"}
                    options={[
                      { label: "pending", value: "pending" },
                      { label: "issued", value: "issued" },
                      { label: "revoked", value: "revoked" },
                      { label: "expired", value: "expired" },
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  name="cert_fingerprint"
                  label="Cert Fingerprint"
                  style={{ minWidth: 320, flex: "1 1 320px" }}
                >
                  <Input
                    readOnly={certMode === "view"}
                    placeholder="SHA256:..."
                  />
                </Form.Item>
              </Flex>

              <Form.Item
                name="cert_public_key"
                label="Certificate Public Key (cert.pub)"
                tooltip="If you paste the signed cert here and save, we call set_ssh_certificate_signed(...) and mark status=issued."
              >
                <TextArea
                  rows={8}
                  readOnly={certMode === "view"}
                  style={{ fontFamily: "monospace" }}
                  placeholder="Paste ssh-ed25519-cert-v01@openssh.com AAAA... here"
                />
              </Form.Item>

              {certMode === "view" && (
                <Space>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(activeCert.cert_public_key)}
                  >
                    Copy
                  </Button>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => {
                      setCertMode("edit");
                      certForm.setFieldsValue({
                        note: activeCert.note ?? "",
                        status: activeCert.status ?? "issued",
                        cert_public_key: activeCert.cert_public_key ?? "",
                        cert_fingerprint: activeCert.cert_fingerprint ?? "",
                      });
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    danger
                    icon={<StopOutlined />}
                    disabled={activeCert.status === "revoked"}
                    onClick={() =>
                      handleRevoke(
                        activeCert.id,
                        `${activeCert.identity} (serial ${activeCert.serial})`,
                      )
                    }
                  >
                    Revoke
                  </Button>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() =>
                      openIssue({
                        ca_id: activeCert.ca_id,
                        subject_key_id: activeCert.subject_key_id,
                        cert_type: activeCert.cert_type,
                        identity: activeCert.identity,
                        principals: activeCert.principals,
                        note: activeCert.note,
                      })
                    }
                  >
                    Issue another like this
                  </Button>
                </Space>
              )}
            </Form>
          </>
        )}
      </ResponsiveEditor>

      {/* Issue Overlay (mobile-friendly) */}
      <ResponsiveEditor
        open={issueOverlayOpen}
        onClose={() => setIssueOverlayOpen(false)}
        title="Issue Certificate (Create Request)"
        onOk={handleIssue}
        okText="Create"
        width={UI.MODAL_WIDE_WIDTH_PX}
      >
        <Form form={issueForm} layout="vertical">
          <Flex gap={12} wrap>
            <Form.Item
              name="cert_type"
              label="Cert Type"
              rules={[{ required: true }]}
              style={{ minWidth: 180, flex: "1 1 180px" }}
            >
              <Select
                options={[
                  { label: "Host", value: "host" },
                  { label: "User", value: "user" },
                ]}
              />
            </Form.Item>

            <Form.Item
              name="ca_id"
              label="CA"
              rules={[{ required: true }]}
              style={{ minWidth: 320, flex: "2 1 320px" }}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={caOptions}
                placeholder="Select CA"
              />
            </Form.Item>
          </Flex>

          <Form.Item
            name="subject_key_id"
            label="Subject Key"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={keyOptions}
              placeholder="Select key to certify"
            />
          </Form.Item>

          <Flex gap={12} wrap>
            <Form.Item
              name="identity"
              label="Identity"
              rules={[{ required: true }]}
              style={{ minWidth: 260, flex: "2 1 260px" }}
            >
              <Input placeholder="web01.example.com or alice" />
            </Form.Item>

            <Form.Item
              name="key_id"
              label="Key ID (optional)"
              style={{ minWidth: 240, flex: "2 1 240px" }}
            >
              <Input placeholder="host:web01.example.com:123" />
            </Form.Item>
          </Flex>

          <Form.Item name="principals" label="Principals (comma-separated)">
            <Input placeholder="web01.example.com, web01" />
          </Form.Item>

          <Flex gap={12} wrap>
            <Form.Item
              name="valid_from"
              label="Valid From"
              style={{ minWidth: 240, flex: "1 1 240px" }}
              initialValue={dayjs()}
            >
              <DatePicker showTime style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="validity"
              label="Validity (interval)"
              tooltip='Any PostgreSQL interval string. Examples: "8 hours", "30 days", "1 year". Default is 1 year.'
              style={{ minWidth: 240, flex: "1 1 240px" }}
              initialValue="1 year"
            >
              <Input placeholder="1 year" />
            </Form.Item>
          </Flex>

          <Flex gap={12} wrap>
            <Form.Item
              name="critical_options_json"
              label="Critical Options (JSON)"
              style={{ minWidth: 300, flex: "1 1 300px" }}
              initialValue="{}"
            >
              <TextArea rows={4} style={{ fontFamily: "monospace" }} />
            </Form.Item>
            <Form.Item
              name="extensions_json"
              label="Extensions (JSON)"
              style={{ minWidth: 300, flex: "1 1 300px" }}
              initialValue="{}"
            >
              <TextArea rows={4} style={{ fontFamily: "monospace" }} />
            </Form.Item>
          </Flex>

          <Form.Item name="note" label="Note">
            <Input />
          </Form.Item>

          <Divider style={{ margin: "8px 0" }} />
          <Text type="secondary">
            This creates a <b>pending</b> certificate row. Your signer
            service/app should produce the actual cert and call{" "}
            <Text code>ssh.set_ssh_certificate_signed</Text>.
          </Text>
        </Form>
      </ResponsiveEditor>
    </div>
  );
};

export default SSHCertControl;
